import { useCallback, useEffect } from 'react'
import * as L from 'leaflet'
import * as turf from '@turf/turf'
import styled from 'styled-components'
import { useLeafletContext } from '@react-leaflet/core'
import { getCircleDrawing } from './ControlledDrawings'
import { DrawControl } from './index'
import BulkSelectIcon from './bulk-select.svg?react'
import { borderRadius, color, fontSize, padding, shadow } from '@evrekadev/evreka-ui-components'
import { UilTimes } from '@iconscout/react-unicons'
import { Icon } from 'components'
import { useTranslation } from 'utils/useTranslation'
import { useMapContext } from 'components/MapV2/MapProvider'
import { MapMarkerProps } from 'components/MapV2/components/ui'

const options: L.PM.ToolbarOptions = {
  drawMarker: false,
  drawCircle: false,
  drawCircleMarker: false,
  drawRectangle: false,
  drawPolygon: false,
  drawPolyline: false,
  drawText: false,
  cutPolygon: false,
  rotateMode: false,
  dragMode: false,
  editMode: false,
  removalMode: false,
}

export const MarkerBulkSelection: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const { t } = useTranslation('components.map')
  const { map } = useLeafletContext()

  const {
    showBulkSelection: isEnabled,
    markers,
    onBulkSelection,
    onToggleBulkSelection,
  } = useMapContext()

  const onDrawEnd: L.PM.DrawEndEventHandler = useCallback(
    (e) => {
      const layers = map.pm.getGeomanLayers() as any

      if (!layers.length) {
        return
      }

      const lastDrawing = layers[layers.length - 1]

      const lastDrawingPolygon =
        e.shape === 'Circle'
          ? turf.feature({
              type: 'Polygon',
              coordinates: [
                getCircleDrawing(lastDrawing).positions.map(([lat, lon]) => [lon, lat]),
              ],
            })
          : lastDrawing.toGeoJSON()

      const foundMarkers: MapMarkerProps[] = []

      lastDrawing.remove()

      markers
        ?.filter((m) => m.selectable)
        ?.forEach((marker: MapMarkerProps) => {
          const position = marker.marker.position as L.LatLngTuple
          const newPosition = [position[1], position[0]]

          const isPointInPolygon =
            lastDrawingPolygon.geometry.type !== 'Point'
              ? turf.booleanPointInPolygon(newPosition, lastDrawingPolygon)
              : false

          if (isPointInPolygon) {
            foundMarkers.push(marker)
          }
        })

      if (onBulkSelection) {
        onBulkSelection(foundMarkers)
      }
    },
    [onBulkSelection, markers],
  )

  useEffect(() => {
    map.on('pm:drawend', onDrawEnd)

    return () => {
      map.off('pm:drawend', onDrawEnd)
    }
  }, [map, onDrawEnd])

  if (!isActive) {
    return null
  }

  return (
    <div>
      <BulkSelectionButton
        isEnabled={isEnabled}
        title={t('bulk_select')}
        onClick={() => {
          map.pm.disableDraw()
          onToggleBulkSelection?.(!isEnabled)
        }}
      >
        {isEnabled ? <Icon icon={UilTimes} /> : <BulkSelectIcon />}
      </BulkSelectionButton>

      {isEnabled && (
        <BulkSelectionContainer className="leaflet-pm-toolbar leaflet-pm-draw leaflet-bar leaflet-control">
          <div className="button-container">
            <button
              className="leaflet-buttons-control-button"
              onClick={(e) => {
                e.preventDefault()
                map.pm.enableDraw('Rectangle')
              }}
            >
              <div className="control-icon leaflet-pm-icon-rectangle"></div>
            </button>
          </div>
          <div className="button-container">
            <button
              className="leaflet-buttons-control-button"
              onClick={(e) => {
                e.preventDefault()
                map.pm.enableDraw('Polygon')
              }}
            >
              <div className="control-icon leaflet-pm-icon-polygon"></div>
            </button>
          </div>
          <div className="button-container">
            <button
              className="leaflet-buttons-control-button"
              onClick={(e) => {
                e.preventDefault()
                map.pm.enableDraw('Circle')
              }}
            >
              <div className="control-icon leaflet-pm-icon-circle"></div>
            </button>
          </div>
        </BulkSelectionContainer>
      )}
      <DrawControl options={options} />
    </div>
  )
}

const BulkSelectionContainer = styled.div`
  width: 36px;
  top: 162px;
  z-index: 1000;
  border-radius: 0 0 4px 4px !important;
  left: 10px;

  button {
    all: unset;
    width: 33px !important;

    .control-icon {
      height: 24px;
    }
  }
`

const BulkSelectionButton = styled.div<{ isEnabled?: boolean }>`
  cursor: pointer;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  ${padding([6])}
  left: 10px;

  width: 36px;
  height: 32px;

  background: ${color('grey50')};
  border: 1px solid ${color('grey300')};

  ${shadow('xs')}
  ${borderRadius(4)}
  ${fontSize('text_xs')};

  position: absolute;
  top: 130px;
  z-index: 1000;

  ${(props) =>
    props.isEnabled &&
    `
      background: ${color('grey100')};
      border-radius: 5px 5px 0 0 !important;
  `}
`
